import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import {
  Users2, Wallet, ClipboardCheck, TrendingUp, Banknote, Share2,
  Activity, ShieldCheck, ShieldAlert, Server,
} from "lucide-react";
import { adminGetMetrics } from "@/lib/afritask";

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" }) : "—");

const PIE_COLORS = ["#B45309", "#10B981", "#F59E0B", "#EF4444", "#D97706", "#F59E0B"];

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    adminGetMetrics().then(setD).catch(() => setD(null));
    const t = setInterval(() => {
      adminGetMetrics().then(setD).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  if (!d) return <p className="text-sm text-neutral-500">Loading metrics…</p>;

  const k = d.kpis;
  const cards = [
    { icon: Users2, tone: "indigo", label: "Total users", value: k.total_users.toLocaleString(), delta: `+${k.users_this_week} this week`, testid: "kpi-users" },
    { icon: Activity, tone: "emerald", label: "Active users (7d)", value: k.active_users_week.toLocaleString(), delta: "with any activity", testid: "kpi-active" },
    { icon: Wallet, tone: "amber", label: "Top-ups (7d)", value: fmtNaira(k.topups_week_ngn), delta: `Lifetime ${fmtNaira(k.revenue_ngn)}`, testid: "kpi-revenue" },
    { icon: ClipboardCheck, tone: "rose", label: "Pending reviews", value: k.pending_reviews.toLocaleString(), delta: "Waiting on admin", testid: "kpi-reviews" },
    { icon: Banknote, tone: "indigo", label: "Pending withdrawals", value: `${k.pending_withdrawals.count} · ${fmtNaira(k.pending_withdrawals.amount_ngn)}`, delta: "In queue", testid: "kpi-withdrawals" },
    { icon: Share2, tone: "emerald", label: "Referrals (7d)", value: `${k.referral_velocity.week_new} new · ${k.referral_velocity.week_activated} act.`, delta: "Referral velocity", testid: "kpi-referrals" },
  ];
  const tones = {
    indigo: "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    rose: "bg-rose-50 text-rose-600",
  };

  const health = d.health;

  return (
    <div className="space-y-6">
      {/* KPI row */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((c) => (
          <Card key={c.label} data-testid={c.testid}>
            <CardContent className="p-4">
              <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tones[c.tone]}`}>
                <c.icon className="h-4 w-4" />
              </span>
              <p className="mt-3 text-[10px] font-medium uppercase tracking-wider text-neutral-500">{c.label}</p>
              <p className="mt-0.5 truncate text-lg font-bold tabular-nums">{c.value}</p>
              <p className="mt-0.5 truncate text-[10px] text-neutral-500">{c.delta}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row 1 */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Top-ups per day (last 30 days)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.series.topups_by_day} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} interval={4} />
              <YAxis stroke="#94a3b8" fontSize={10} tickFormatter={(v) => `₦${v/1000}k`} />
              <Tooltip formatter={(v) => fmtNaira(v)} />
              <Line type="monotone" dataKey="amount_ngn" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Task entries per day (last 30 days)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={d.series.entries_by_day} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} interval={4} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke="#B45309" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Task type distribution (last 30 days)">
          {d.series.task_type_distribution.length === 0 ? (
            <p className="py-10 text-center text-sm text-neutral-500">No entries in the last 30 days.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={d.series.task_type_distribution}
                  dataKey="count"
                  nameKey="type"
                  outerRadius={80}
                  label={(e) => e.type}
                >
                  {d.series.task_type_distribution.map((e, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Selection buckets (last 4 weeks)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.series.bucket_by_week}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" stroke="#94a3b8" fontSize={10} />
              <YAxis stroke="#94a3b8" fontSize={10} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="A" stackId="a" fill="#B45309" name="Bucket A (Elite)" />
              <Bar dataKey="B" stackId="a" fill="#D97706" name="Bucket B (Loyal)" />
              <Bar dataKey="C" stackId="a" fill="#10B981" name="Bucket C (New)" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Activity + Health */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold">Recent activity</h3>
            <ul className="max-h-72 divide-y divide-neutral-100 overflow-y-auto" data-testid="activity-feed">
              {d.activity.length === 0 && (
                <li className="py-6 text-center text-xs text-neutral-500">Quiet on the platform. Nothing yet.</li>
              )}
              {d.activity.map((a, i) => (
                <li key={i} className="flex items-center justify-between gap-3 py-2 text-xs">
                  <span className="min-w-0 truncate">{a.title}</span>
                  <span className="shrink-0 text-neutral-400">{fmtWhen(a.at)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <h3 className="mb-3 text-sm font-semibold">Health checks</h3>
            <ul className="space-y-2 text-xs">
              <li className="flex items-center justify-between rounded-lg border border-neutral-100 p-2.5" data-testid="health-mongo">
                <span className="inline-flex items-center gap-2 text-neutral-700">
                  <Server className="h-3.5 w-3.5" /> MongoDB
                </span>
                <Badge variant="outline" className={health.mongo_ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}>
                  {health.mongo_ok ? <ShieldCheck className="mr-1 h-3 w-3" /> : <ShieldAlert className="mr-1 h-3 w-3" />}
                  {health.mongo_ok ? `${health.mongo_latency_ms}ms` : "Down"}
                </Badge>
              </li>
              <li className="flex items-center justify-between rounded-lg border border-neutral-100 p-2.5" data-testid="health-paystack">
                <span className="inline-flex items-center gap-2 text-neutral-700">
                  <TrendingUp className="h-3.5 w-3.5" /> Paystack
                </span>
                <Badge variant="outline" className={health.paystack_ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700"}>
                  {health.paystack_ok ? "Reachable" : "Unreachable"}
                </Badge>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h3 className="mb-3 text-sm font-semibold text-neutral-800">{title}</h3>
        {children}
      </CardContent>
    </Card>
  );
}
